import { useAuth } from 'react-oidc-context';
import { useEffect, useState, useRef } from 'react';
import { isLocalDevelopment, isStandaloneMode } from './lib/environmentUtils';

const EVO_PLATFORM_ORIGINS = [
    'https://go.accessacloud.com',
    'https://preprodgo.accessacloud.com',
    'https://testgo.accessacloud.com',
    'https://demogo.accessacloud.com',
];

const requestReauthentication = (returnUrl: string = '/') => {
    const message = {
        type: 'RequestReauthentication',
        url: returnUrl,
    };
    
    EVO_PLATFORM_ORIGINS.forEach(origin => {
        try {
            window.parent.postMessage(JSON.stringify(message), origin);
        } catch (err) {
            console.debug(`ProjectApproval_ Failed to post message to ${origin}:`, err);
        }
    });
    
    console.log('ProjectApproval_ Requested re-authentication from EVO platform');
};

const useDevIdentity = () => {
    return {
        requestPlatformReauth: () => {},
        getAccessToken: async () => 'dev-token',
        loading: false,
        error: null,
        isAuthenticated: true,
        isRequestingReauth: false,
        user: null
    };
};

const useAuthIdentity = () => {
    const auth = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isRequestingReauth, setIsRequestingReauth] = useState(false);
    const _getAccessTokenPromise = useRef<Promise<string> | null>(null);
    const hasAttemptedSilentSignIn = useRef(false);

    const signinSilentRef = useRef(auth.signinSilent);
    signinSilentRef.current = auth.signinSilent;

    useEffect(() => {
        const performSilentSignIn = async () => {
            if (hasAttemptedSilentSignIn.current) {
                return;
            }
            hasAttemptedSilentSignIn.current = true;

            try {
                setLoading(true);
                setError(null);
                
                if (!auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
                    await signinSilentRef.current();
                }
            } catch (err) {
                console.error('ProjectApproval_ Silent sign-in failed:', err);
                setIsRequestingReauth(true);
                requestReauthentication(window.location.pathname);
            } finally {
                setLoading(false);
            }
        };

        performSilentSignIn();
    }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading]);

    useEffect(() => {
        if (!auth.isLoading) {
            setLoading(false);
        }
    }, [auth.isLoading]);

    const requestPlatformReauth = () => {
        setIsRequestingReauth(true);
        requestReauthentication(window.location.pathname);
    };

    const getAccessToken = async (): Promise<string> => {
        if (_getAccessTokenPromise.current) {
            return _getAccessTokenPromise.current;
        }

        _getAccessTokenPromise.current = (async () => {
            try {
                const isTokenExpired = auth.user?.expired ?? true;
                
                if (!auth.user || isTokenExpired) {
                    await auth.signinSilent();
                }

                if (!auth.user?.access_token) {
                    throw new Error('Unable to obtain access token');
                }

                return auth.user.access_token;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to get access token';
                throw new Error(errorMessage);
            } finally {
                _getAccessTokenPromise.current = null;
            }
        })();

        return _getAccessTokenPromise.current;
    };

    return {
        requestPlatformReauth,
        getAccessToken,
        loading: loading || auth.isLoading,
        error: error || (auth.error ? auth.error.message : null),
        isAuthenticated: auth.isAuthenticated,
        isRequestingReauth,
        user: auth.user
    };
};

const shouldBypassAuth = (): boolean => isLocalDevelopment() || isStandaloneMode();

export const useIdentity = shouldBypassAuth() ? useDevIdentity : useAuthIdentity;
